import { InsuranceBillingPeriod } from '../models/insurance-models/InsuranceBillingPeriodModel'
import { InsuranceOptionModel } from '../models/insurance-models/InsuranceOptionModel'
import { FirebaseConfigKey, FirebaseService } from './FirebaseService'

class DiscountService {
    calculateMonthlyToAnnualSavings = (
        monthlyInsuranceOffer,
        annualInsuranceOffer
    ) => {
        let dronesDiff =
            monthlyInsuranceOffer.dronePremium -
            annualInsuranceOffer.dronePremium / 12
        let equipmentDiff =
            monthlyInsuranceOffer.equipmentPremium -
            annualInsuranceOffer.equipmentPremium / 12
        return ((dronesDiff + equipmentDiff) * 12).toFixed(2)
    }

    calculateDiscount(
        currentInsurance: InsuranceOptionModel,
        totalDiscount,
        isHullAvailable,
        drones,
        equipment
    ) {
        let discount = currentInsurance.totalDiscount

        let estimatedPrice =
            currentInsurance.price / currentInsurance.purchaseDiscountMultiplier

        if (currentInsurance.dronePremium)
            estimatedPrice -= currentInsurance.dronePremium
        if (currentInsurance.equipmentPremium)
            estimatedPrice -= currentInsurance.equipmentPremium

        let fullPrice = estimatedPrice / discount

        let hullDiscount =
            currentInsurance.avgFlightScoreMultiplier *
            currentInsurance.operatorsDiscountMultiplier
        let operatorDiscount = currentInsurance.operatorsDiscountMultiplier
        let hullFullPriceBeforeDiscount =
            currentInsurance.dronePremium / hullDiscount +
            currentInsurance.equipmentPremium
        let hullFullPrice =
            currentInsurance.dronePremium + currentInsurance.equipmentPremium
        let hullReduction = hullFullPriceBeforeDiscount - hullFullPrice
        let operatorDiscountPrice = hullFullPrice * (1 - operatorDiscount)
        let hullDiscountPrice = hullFullPrice * (1 - operatorDiscount)
        let purchaseDiscount = currentInsurance.purchaseDiscountMultiplier
        let hullCoverageDiscountMultiplier =
            currentInsurance.hullCoverageDiscountMultiplier
        let hullCoverageVolumeDiscountMultiplier =
            currentInsurance.hullCoverageVolumeDiscountMultiplier

        let priceWithoutPurchaseDiscounts =
            currentInsurance.price / purchaseDiscount
        let hullCoverageDiscountPrice =
            priceWithoutPurchaseDiscounts * (1 - hullCoverageDiscountMultiplier)
        let hullCoverageVolumeDiscountPrice =
            (currentInsurance.price / hullCoverageVolumeDiscountMultiplier) *
            (1 - hullCoverageVolumeDiscountMultiplier)
        let totalPurchaseDiscount =
            priceWithoutPurchaseDiscounts * (1 - purchaseDiscount)

        let totalDiscountPrice = fullPrice * (1 - discount)
        let finalPrice = currentInsurance.price
        if (isHullAvailable && hullDiscountPrice) {
            totalDiscountPrice = totalDiscountPrice + hullDiscountPrice
        }
        if (!isHullAvailable) {
            if (
                currentInsurance.dronePremium ||
                currentInsurance.equipmentPremium
            )
                finalPrice =
                    finalPrice -
                    currentInsurance.dronePremium -
                    currentInsurance.equipmentPremium
        }

        if (currentInsurance.promoCreditsUsed > 0)
            finalPrice = finalPrice - currentInsurance.promoCreditsUsed

        let taxes = 0
        if (currentInsurance.taxesMultiplier > 1) {
            let priceWithTaxes = finalPrice * currentInsurance.taxesMultiplier
            taxes = priceWithTaxes - finalPrice
            finalPrice = priceWithTaxes
        }

        return {
            liability_price:
                currentInsurance.liabilityLimitPremium +
                currentInsurance.liabilityLimitDiscount,
            liability_discount: currentInsurance.liabilityLimitDiscount,
            liability_discount_percent:
                currentInsurance.liabilityLimitDiscountPercentage,
            operator_discount_price: operatorDiscountPrice,
            operator_discount_percent: (1 - hullDiscount).toFixed(2) * 100,
            hull_price: hullFullPrice.toLocaleStringToFixed(),
            hull_percent: (1 - hullDiscount).toFixed(3) * 100,
            hull_discount: hullDiscountPrice.toFixed(2),
            hull_total_price: hullFullPrice.toLocaleStringToFixed(),
            hull_total_price_before_discount:
                hullFullPriceBeforeDiscount.toLocaleStringToFixed(),
            hull_reduction: hullReduction.toLocaleStringToFixed(),
            equipment_price: currentInsurance.equipmentPremium
                ? currentInsurance.equipmentPremium.toFixed(2)
                : 0,
            total_discount: totalDiscountPrice.toFixed(2),
            final_price: finalPrice.toLocaleStringToFixed(),
            promo_credits: currentInsurance.promoCreditsUsed,
            hull_coverage_volume_discount_percent:
                (
                    1 - currentInsurance.hullCoverageVolumeDiscountMultiplier
                ).toFixed(2) * 100,
            hull_coverage_volume_discount:
                hullCoverageVolumeDiscountPrice.toFixed(2),
            hull_coverage_discount_percent:
                (1 - currentInsurance.hullCoverageDiscountMultiplier).toFixed(
                    2
                ) * 100,
            hull_coverage_discount: hullCoverageDiscountPrice.toFixed(2),
            purchase_discount_percent:
                (1 - currentInsurance.purchaseDiscountMultiplier).toFixed(2) *
                100,
            purchase_discount: totalPurchaseDiscount.toFixed(2),
            taxes: taxes.toFixed(2),
            medical_expense_limit_premium:
                currentInsurance.medicalExpenseLimitPremium,
            personal_injury_limit_premium:
                currentInsurance.personalInjuryLimitPremium,
            indoor_premium: currentInsurance.indoorCoveragePremium,
            territorial_limits_premium:
                currentInsurance.territorialLimitsPremium,
            drones_deductible: currentInsurance.drones
                ? currentInsurance.drones.map((t) => t.premium)
                : [10],
            equipment_deductible: currentInsurance.equipment
                ? currentInsurance.equipment.map((t) => t.premium)
                : [10],
            drone_premiums_before_discount: currentInsurance.drones.map(
                (q) => q.premium / hullDiscount
            ),
            additional_custom_premium: currentInsurance.additionalCustomPremium,
        }
    }
}

export default new DiscountService()
